// @flow
import type { LocationShape } from "react-router";
import React, { Fragment } from "react";
import { useHistory } from "react-router-dom";
import { Tooltip } from "react-tippy";
import cx from "classnames";
import { connect } from "react-redux";
import glamorous from "glamorous";
import { result } from "lodash";
import t from "common/i18n";
import { asAbsolute } from "root/routes/utils";
import { selectLocation } from "root/selectors";
import MenuButton from "root/components/MenuButton/MenuButton";
import UserBlock from "root/components/UserBlock";
import SearchBox, { SEARCH_BOX_MODES } from "root/components/SearchBox";
import Tabs from "root/components/Tabs/Tabs";
import SVG_ICONS from "~/assets/icons/icons";

import "./TabHeader.scss";

type OwnProps = {|
    searchTerm?: string,
    source: string,
    showSearch?: boolean,
    searchOptions?: Array<string>, /* if defined will add a drop menu on the left side of the search field */
    selectedSearchOption?: ?string,
    searchOptionHandler?: Function,
    onLogout: Function,
    searchTermHandler?: Function,
    searchClickHandler?: Function,
    userName: ?string,
    navItems?: Object[],
    title?: any,
    children?: any,
    searchInputPlaceholder?: string,
    expandSearch?: boolean,
    hideBackButton?: boolean,
    showBackButton?: boolean,
    titleCenter?: ?string,
    showClearButton?: boolean,
    onClearClickHandler?: Function,
    clearBtnTip?: string,
    onBackNavigation?: Function,
    onEscClickHandler?: Function,
|};

type Props = {
    ...OwnProps,
    location?: LocationShape,
};

export const TabHeader = (props: Props) => {
	const history = useHistory();

	const locPath = (props.location && props.location.pathname) || "";
	const inSearch = props.showSearch
            && (!!~locPath.indexOf("search") || props.expandSearch);
	const userName = props.userName || "";

	const onSearchClick = () => {
		if (inSearch) {
			props.searchClickHandler && props.searchClickHandler();
		} else {
			history.push({
				...props.location,
				pathname: asAbsolute(props.source, "search"),
			});
		}
	};

	const onBackClick = () => {
		history.goBack();
		props.onBackNavigation && props.onBackNavigation();
	};

	const renderTabs = () => (!inSearch && props.navItems ? (
		<Tabs
			items={props.navItems}
			className="self-end"
			key="subhead-links"
		/>
	) : null);

	const renderBackButton = () => (props.showBackButton || (inSearch && !props.hideBackButton) ? (
		<Fragment key="subhead-back">
			<MenuButton
				className="pointer back-button pt-3 pr-3 pb-3 pl-3"
				iconSize={22}
				icon={SVG_ICONS.BACK}
				onClick={onBackClick}
			/>
			<div className="divider bc-window-border mr-3" />
		</Fragment>
	) : null);

	const renderSearchBox = () => (props.showSearch ? (
		<SearchBox
			key="tabheader_searchbox"
			mode={
				inSearch
					? SEARCH_BOX_MODES.EXPANDED
					: SEARCH_BOX_MODES.BUTTON
			}
			inputPlaceholder={
				props.searchInputPlaceholder || t("search_placeholder")
			}
			searchHandler={onSearchClick}
			termChangeHandler={props.searchTermHandler}
			searchOptions={props.searchOptions}
			selectedSearchOption={props.selectedSearchOption}
			searchOptionHandler={props.searchOptionHandler}
			showClearButton={props.showClearButton}
			onClearClickHandler={props.onClearClickHandler}
			onEscClickHandler={props.onEscClickHandler}
			clearBtnTip={props.clearBtnTip}
			value={props.searchTerm}
		/>
	) : null);

	const renderTitle = () => !inSearch
        && !props.navItems
        && props.title && (
		<span className="tab-header-title pl6 pr6" key="th_title">
			{result(props, "title", null)}
		</span>
	);

	const renderTitleCenter = () => (props.titleCenter ? (
		<Tooltip
			key={props.titleCenter}
			title={props.titleCenter}
			arrow
			animation="scale"
			delay={250}
			duration={200}
			hideDelay={0}
			size="small"
			className="tip title-center"
		>
			<span key="title-center">{props.titleCenter}</span>
		</Tooltip>
	) : null);

	const getHeaderContent = () => [
		renderTitle(),
		renderTabs(),
		renderBackButton(),
		renderTitleCenter(),
	];

	return (
		<glamorous.Div
			boxShadow="0 2px 4px -2px rgba(0,0,0,0.5)"
			className={cx(
				"TabHeader",
				"flex flex-none items-center bb bg-window bc-window-border ",
				{
					"search-mode": inSearch,
					"no-search": !inSearch,
				},
			)}
		>
			{props.children && React.Children.count(props.children)
				? props.children
				: getHeaderContent()}
			<div className="flex-grow-1 tr">{renderSearchBox()}</div>
			<UserBlock
				name={userName}
				onLogout={props.onLogout}
				key="tabheader_userblock"
			/>
		</glamorous.Div>
	);
};

const mapState = (state) => ({ location: selectLocation(state) });

export default connect<Props, OwnProps, _, _, _, _>(
	mapState,
	null,
)(TabHeader);
